import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Spacer } from '@chakra-ui/react';
import { useController } from 'pages/Meetings/hooks/useController';
import { formatShortDate } from 'utils/dateFormat';

import Footer from 'components/atom/Footer';
import Button from 'components/atom/Form/Button';
import MySelect from 'components/atom/Form/Select';
import TextArea from 'components/atom/Form/Textarea';
import { Wrapper } from 'components/atom/Grid';
import Loader from 'components/atom/Loader';
import Text from 'components/atom/Text/styles';
import AvatarInvitation from 'components/molecule/AvatarInvitational';
import HeaderContent from 'components/molecule/HeaderContent';

import {
  ButtonDay,
  Container,
  ContainerDays,
  ContainerForm,
  Content,
  ContentForm,
  InterestsContainer,
  PreviewInformation,
  Subtitle,
  Tag,
  Title,
} from './styles';

const Invite: React.FC = () => {
  const { t } = useTranslation();

  const {
    isLoading,
    setPageActive,
    prospectionData,
    prospectionListData,
    setInvitationDataSuggested,
    scheduleData,
    hoursAvailable,

    formik: { setFieldValue, values, handleChange, resetForm, errors, touched, handleSubmit },
  } = useController();

  const handleClose = useCallback(() => {
    setPageActive('schedule');
    setInvitationDataSuggested(null);
    resetForm();
  }, [resetForm, setInvitationDataSuggested, setPageActive]);

  const formatInterests = useMemo(
    () => prospectionListData?.interests?.map((item) => item.name).join(', '),
    [prospectionListData?.interests],
  );

  useEffect(() => {
    const keys = Object.keys(scheduleData || {});

    setFieldValue('date', keys.length > 0 ? keys[0] : '');
  }, [scheduleData, setFieldValue]);

  return (
    <Container>
      <HeaderContent title={t('meetings:invite.title')} onClose={handleClose} />

      <Content>
        <Wrapper>
          <Text p16 padding="0 0 4rem">
            {t('meetings:preview.info')}
          </Text>
        </Wrapper>

        <PreviewInformation>
          <Wrapper>
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <AvatarInvitation
                  type="invite"
                  name={prospectionData?.user.fullname}
                  src={prospectionData?.user.avatar}
                  size="lg"
                />

                <Box>
                  <Title>
                    <strong>{prospectionData?.user.fullname} </strong>
                  </Title>

                  <Text p16 lineHeight={26} textAlign="center">
                    {prospectionData?.user.role}
                  </Text>

                  <Text weight="bold" lineHeight={18} textAlign="center">
                    {prospectionData?.user.companyFantasyName}
                  </Text>
                </Box>

                {formatInterests && (
                  <InterestsContainer>
                    <Text weight="bold">{`${t('meetings:meeting.interests')}  ${
                      prospectionData?.user.name
                    }:`}</Text>

                    <Text>{formatInterests}</Text>
                  </InterestsContainer>
                )}
              </>
            )}
          </Wrapper>
        </PreviewInformation>

        <Spacer mt={3} />

        {scheduleData && !isLoading ? (
          <>
            <ContentForm>
              <Wrapper>
                <Box>
                  <Subtitle>{t('meetings:setupMeeting.subtitle')}</Subtitle>

                  <ContainerDays>
                    {Object.keys(scheduleData).map((item, index) => (
                      <ButtonDay
                        key={index}
                        selected={values.date === item}
                        onClick={() => {
                          setFieldValue('date', item);
                          setFieldValue('hour', '');
                        }}
                      >
                        {item && formatShortDate(item)}
                      </ButtonDay>
                    ))}
                  </ContainerDays>

                  <ContainerForm>
                    <Box display="flex" alignItems="center" gap={4}>
                      <Text weight="600">{t('meetings:setupMeeting.label.hour')}</Text>

                      <MySelect
                        id="hour"
                        name="hour"
                        placeholder={t('meetings:setupMeeting.label.select')}
                        options={hoursAvailable || []}
                        value={values.hour}
                        onChange={(newValue) => setFieldValue('hour', newValue)}
                        isDisabled={!values.date}
                        canSearch={false}
                      />
                    </Box>

                    <Box display="flex" alignItems="center" mt={4} gap={4}>
                      <Text weight="600">{`${t('meetings:setupMeeting.label.local')}`}</Text>

                      <Tag>SET Centro-Oeste</Tag>
                    </Box>
                  </ContainerForm>
                </Box>
              </Wrapper>
            </ContentForm>

            <Spacer mt={3} />

            <ContentForm>
              <Wrapper>
                <Text weight="600" padding="0 0 .7rem">
                  {t('meetings:setupMeeting.label.message')}
                </Text>

                <TextArea
                  className="textarea"
                  name="message"
                  value={values.message}
                  onChange={handleChange}
                  isDisabled={!values.date}
                  height={9.2}
                  error={(touched.message && errors.message && errors.message) || undefined}
                />

                <Button
                  theme="blue03"
                  text={t('meetings:actions.proceed')}
                  disabledUppercase
                  isDisabled={!values.date || !values.hour}
                  onClick={handleSubmit}
                />
              </Wrapper>
            </ContentForm>
          </>
        ) : (
          <Wrapper>
            {!isLoading && (
              <Text weight="600" padding="2rem 0">
                {t('meetings:setupMeeting.empty')}
              </Text>
            )}
          </Wrapper>
        )}
      </Content>

      <Footer />
    </Container>
  );
};

export default Invite;
